import { useState, useEffect } from "react";
import { Loader2, Send } from "lucide-react";
import { colors, fonts } from "../../styles/theme";
import { fetchContactTimeline } from "../../services/api";
import { timeAgo, TIMELINE_ICONS, TIMELINE_FILTERS } from "./constants";

export default function ActivityTimeline({ contactId }) {
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filter, setFilter] = useState("all");

  const loadEvents = (typeFilter, offset = 0, append = false) => {
    const setLoadState = offset === 0 ? setLoading : setLoadingMore;
    setLoadState(true);
    const COMPOUND_FILTERS = {
      webcast: "webcast_registered,webcast_attended",
      newsletter: "newsletter_open,newsletter_click",
      email: "email_sent,email_received",
    };
    const types = typeFilter === "all" ? "all" : (COMPOUND_FILTERS[typeFilter] || typeFilter);
    fetchContactTimeline(contactId, { limit: 20, offset, types })
      .then((data) => {
        setEvents((prev) =>
          append ? [...prev, ...(data.events || [])] : data.events || [],
        );
        setTotal(data.total || 0);
      })
      .catch(() => {
        if (!append) setEvents([]);
      })
      .finally(() => setLoadState(false));
  };

  useEffect(() => {
    loadEvents(filter);
  }, [contactId, filter]);

  const handleLoadMore = () => {
    loadEvents(filter, events.length, true);
  };

  return (
    <div style={{ marginBottom: 24 }}>
      <div
        style={{
          fontSize: 11,
          color: colors.textSecondary,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          marginBottom: 8,
        }}
      >
        Activity Timeline {!loading && total > 0 && `(${total})`}
      </div>

      <div
        style={{ display: "flex", gap: 4, marginBottom: 10, flexWrap: "wrap" }}
      >
        {TIMELINE_FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            style={{
              padding: "3px 8px",
              borderRadius: 4,
              border: `1px solid ${filter === f.id ? colors.accent + "66" : colors.border}`,
              background: filter === f.id ? colors.accentDim : "transparent",
              color: filter === f.id ? colors.accent : colors.textSecondary,
              fontSize: 10,
              fontFamily: fonts.body,
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.15s",
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div
          style={{
            padding: 16,
            textAlign: "center",
            background: colors.bg,
            borderRadius: 6,
            border: `1px solid ${colors.border}`,
          }}
        >
          <Loader2 size={14} color={colors.textSecondary} className="spin" />
        </div>
      ) : events.length === 0 ? (
        <div
          style={{
            padding: 16,
            fontSize: 12,
            color: colors.textSecondary,
            background: colors.bg,
            borderRadius: 6,
            border: `1px solid ${colors.border}`,
            textAlign: "center",
          }}
        >
          No activity yet
        </div>
      ) : (
        <div
          style={{
            background: colors.bg,
            borderRadius: 6,
            border: `1px solid ${colors.border}`,
            overflow: "hidden",
          }}
        >
          {events.map((evt, i) => {
            const iconInfo = TIMELINE_ICONS[evt.type] || {
              icon: Send,
              color: colors.textSecondary,
            };
            const IconComp = iconInfo.icon;
            return (
              <div
                key={evt.id || i}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 8,
                  padding: "8px 12px",
                  borderBottom:
                    i < events.length - 1
                      ? `1px solid ${colors.border}`
                      : "none",
                }}
              >
                <div
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: 4,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: iconInfo.color + "18",
                    flexShrink: 0,
                    marginTop: 1,
                  }}
                >
                  <IconComp size={11} color={iconInfo.color} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 12,
                      color: colors.text,
                      lineHeight: 1.4,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {evt.description}
                  </div>
                  <div
                    style={{
                      fontSize: 10,
                      color: colors.textSecondary,
                      fontFamily: fonts.mono,
                    }}
                  >
                    {timeAgo(evt.timestamp)}
                  </div>
                </div>
              </div>
            );
          })}

          {events.length < total && (
            <button
              onClick={handleLoadMore}
              disabled={loadingMore}
              style={{
                width: "100%",
                padding: "8px 0",
                border: "none",
                borderTop: `1px solid ${colors.border}`,
                background: "transparent",
                color: colors.accent,
                fontSize: 11,
                fontFamily: fonts.body,
                fontWeight: 600,
                cursor: loadingMore ? "wait" : "pointer",
              }}
            >
              {loadingMore
                ? "Loading..."
                : `Load more (${total - events.length} remaining)`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
